<script setup lang="ts">
/**
 * #694 AC4: the recurring evaluation trend for Operations → Memory (CTO decision 2: extended into the
 * existing Memory page rather than a new one), showing one row per recorded run with its score and whether a
 * drop is an explained change or a regression an operator should notice.
 *
 * Every row carries the configuration, cost and data volume the run executed under next to its score, because
 * that is what makes the trend readable: a score that fell after the model changed is a different fact from a
 * score that fell with nothing else moving, and the row says which it is rather than leaving the reader to
 * infer it from the number (AC2).
 */
import { hadNoProbes, type MemoryEvalRegressionLabel, type MemoryEvalRunSummary } from '@zerohuman/shared'

defineProps<{ runs: MemoryEvalRunSummary[] }>()

/** What each label means, in the words a reader needs rather than as a bare enum value. */
const LABEL_TEXT: Record<MemoryEvalRegressionLabel, string> = {
    unexplained_regression: 'Regression — nothing recorded explains this drop',
    explained: 'Explained by the model, cost or data-volume change recorded with it, or by a change to its probes',
    improvement: 'Improvement on the previous run',
    no_change: 'No change from the previous run',
}

/**
 * A run that asked no probes — the enterprise's memory could not supply one — is not a score of 0 and must not
 * read as "memory is not helping" (#694 CTO decision 9), so it says what it is instead of showing a number.
 */
const NO_PROBES_TEXT = 'Not enough memory to evaluate'

/** The first run of an enterprise has nothing to compare with, and says so rather than reading as unchanged. */
function labelText(label: MemoryEvalRegressionLabel | null): string {
    return label === null ? 'First recorded run — no previous run to compare with' : LABEL_TEXT[label]
}

/** The score to two places: a run-over-run comparison, not a measurement worth more precision than that. */
function score(run: MemoryEvalRunSummary): string {
    return run.aggregateScore.toFixed(2)
}

/** The timestamp as stored: UTC, to the minute, matching how Memory renders every other time. */
function recordedAt(run: MemoryEvalRunSummary): string {
    return `${run.createdAt.slice(0, 10)} ${run.createdAt.slice(11, 16)} UTC`
}
</script>

<template>
    <section class="flex flex-col gap-2" aria-label="Memory evaluation trend">
        <h2 class="text-sm font-semibold">Evaluation runs</h2>
        <p class="text-xs text-muted-foreground">
            Each run asks recall probes built from this enterprise's own memory twice — once with memory, once
            without — under one model configuration, and scores how much of each probed entry memory brought back.
            The probes are kept from run to run, so the score moves when memory does. Oldest first.
        </p>
        <ul class="flex flex-col divide-y rounded-lg border">
            <li
                v-for="run in runs"
                :key="run.id"
                data-testid="memory-eval-run-row"
                :data-run-id="run.id"
                :data-regression-label="run.regressionLabel ?? 'first_run'"
                :data-no-probes="hadNoProbes(run) ? 'true' : undefined"
                class="flex flex-wrap items-center justify-between gap-2 p-3 text-sm"
                :class="run.regressionLabel === 'unexplained_regression' ? 'text-destructive' : undefined"
            >
                <span v-if="hadNoProbes(run)" class="font-medium text-muted-foreground">{{ NO_PROBES_TEXT }}</span>
                <span v-else class="font-medium">Score {{ score(run) }}</span>
                <span class="text-xs">{{ labelText(run.regressionLabel) }}</span>
                <span class="text-xs text-muted-foreground">
                    {{ recordedAt(run) }} · {{ run.config.model }} · {{ run.config.costCents }}c ·
                    {{ run.config.dataVolumeBytes }} bytes
                </span>
            </li>
            <li v-if="runs.length === 0" class="p-3 text-sm text-muted-foreground">
                No evaluation run has been recorded yet.
            </li>
        </ul>
    </section>
</template>
